import * as React from "react";
import Grid from "@mui/material/Grid";
import {
  Typography,
  Box,
  Container,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  Divider,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Autocomplete,
  Chip,
  Alert
} from "@mui/material";
import { Table, Space, Modal, Button as AntButton, notification } from "antd";
import { isMobile } from "react-device-detect";
import {
  antTableWrapperSx,
  defaultPaginatedTableProps,
} from "../../app/components/AntTableStyles";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { MainInput, main_button, sec_button, centered_flex_box } from "../../app/components/Styles";
import { getOrders, actionRTOInbound, getProducts } from "../../app/store/actions/imsAdminActions";
import ImageComponent from "../../app/components/ImageComponent";
import { Search, FilterList, FilterListOff, Close } from "@mui/icons-material";
import moment from "moment";
import { buildRtoReceivePayload } from "./rtoReceivePayload";
import { clampFaultyQuantity, okQuantityFromReceive } from "../../app/components/inboundFaultySplit";
import InboundFaultySplitFields from "../../app/components/InboundFaultySplitFields";

export const TakeActionRTO = ({ 
  getOrders, 
  actionRTOInbound, 
  getProducts,
  order, 
  products,
  isLoading, 
  user 
}) => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [orderItemData, setOrderItemData] = React.useState({});
  const [selectedOrderItems, setSelectedOrderItems] = React.useState([]);
  const [productsNotInOrder, setProductsNotInOrder] = React.useState({});
  const [selectedProducts, setSelectedProducts] = React.useState([]);
  const [selectedProductDetails, setSelectedProductDetails] = React.useState({});
  const [productsPage, setProductsPage] = React.useState(1);
  const [hasFetchedOrder, setHasFetchedOrder] = React.useState(false);
  const [hasFetchedProducts, setHasFetchedProducts] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [showFilters, setShowFilters] = React.useState(false);
  const [productModalVisible, setProductModalVisible] = React.useState(false);
  const [selectedProductForModal, setSelectedProductForModal] = React.useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
  const [pendingQuantityUpdate, setPendingQuantityUpdate] = React.useState({
    quantity: null,
    itemId: null,
    sentQuantity: null,
  });
  // AWB lines where a different SKU was physically received (qty must be 0 on orderItems)
  const [differentSkuByItem, setDifferentSkuByItem] = React.useState({});
  // orderItemId -> InventoryProduct ids selected as substitutes for that AWB line
  const [lineSubstitutes, setLineSubstitutes] = React.useState({});
  const [doubleCreditConfirmOpen, setDoubleCreditConfirmOpen] = React.useState(false);
  const [pendingSubmitPayload, setPendingSubmitPayload] = React.useState(null);

  const orderItems = order?.orderItems || [];

  React.useEffect(() => {
    if (orderId && !hasFetchedOrder) {
      getOrders({ orderId }, navigate);
      setHasFetchedOrder(true);
    }
  }, [orderId, hasFetchedOrder]);

  React.useEffect(() => {
    // Initialize order items when orderItems are loaded
    if (orderItems && orderItems.length > 0) {
      const initialData = {};
      const initialSelected = [];
      orderItems.forEach((item) => {
        initialData[item._id] = {
          quantityReceived: item.quantityOrdered || item.quantity || 0,
          faultyQuantity: 0
        };
        initialSelected.push(item._id);
      });
      setOrderItemData(initialData);
      setSelectedOrderItems(initialSelected);
    }
  }, [orderItems]);

  React.useEffect(() => {
    // Fetch partner products when order is loaded
    if (order && order.vendor && !hasFetchedProducts) {
      getProducts({ 
        page: productsPage, 
        limit: 100, 
        vendorId: order.vendor._id || order.vendor 
      });
      setHasFetchedProducts(true);
    }
  }, [order, hasFetchedProducts]);

  React.useEffect(() => {
    // Fetch products when page changes
    if (order && order.vendor && hasFetchedProducts) {
      getProducts({ 
        page: productsPage, 
        limit: 100, 
        vendorId: order.vendor._id || order.vendor,
        search: search || undefined
      });
    }
  }, [productsPage]);

  const applyOrderItemQuantity = (itemId, quantityReceived) => {
    setOrderItemData((prev) => {
      const current = prev[itemId] || { quantityReceived: 0, faultyQuantity: 0 };
      return {
        ...prev,
        [itemId]: {
          ...current,
          quantityReceived,
          faultyQuantity: clampFaultyQuantity(quantityReceived, current.faultyQuantity),
        },
      };
    });
  };

  const handleOrderItemQuantityChange = (itemId, value) => {
    const numValue = parseInt(value) || 0;
    const orderItem = orderItems?.find((item) => item._id === itemId);
    const sentQuantity = orderItem?.quantityOrdered || orderItem?.quantity || 0;

    if (numValue < 0) {
      applyOrderItemQuantity(itemId, 0);
      return;
    }

    // Allow receiving more than sent, but require admin acknowledgment
    if (numValue > sentQuantity) {
      setPendingQuantityUpdate({ quantity: numValue, itemId, sentQuantity });
      setConfirmDialogOpen(true);
      return;
    }

    applyOrderItemQuantity(itemId, numValue);
  };

  const handleConfirmQuantityUpdate = () => {
    if (pendingQuantityUpdate.itemId != null && pendingQuantityUpdate.quantity != null) {
      applyOrderItemQuantity(pendingQuantityUpdate.itemId, pendingQuantityUpdate.quantity);
    }
    setConfirmDialogOpen(false);
    setPendingQuantityUpdate({ quantity: null, itemId: null, sentQuantity: null });
  };

  const handleCancelQuantityUpdate = () => {
    setConfirmDialogOpen(false);
    setPendingQuantityUpdate({ quantity: null, itemId: null, sentQuantity: null });
  };

  const handleOrderItemFaultyQuantityChange = (itemId, value) => {
    setOrderItemData((prev) => {
      const current = prev[itemId] || { quantityReceived: 0, faultyQuantity: 0 };
      return {
        ...prev,
        [itemId]: {
          ...current,
          faultyQuantity: clampFaultyQuantity(current.quantityReceived, value),
        },
      };
    });
  };

  const ensureProductInNotInOrder = (product, quantityReceived, faultyQuantity = 0) => {
    if (!product?._id) return;
    const productId = product._id;
    setSelectedProducts((prev) => (prev.includes(productId) ? prev : [...prev, productId]));
    setSelectedProductDetails((prev) => ({
      ...prev,
      [productId]: product,
    }));
    setProductsNotInOrder((prev) => ({
      ...prev,
      [productId]: {
        quantityReceived: prev[productId]?.quantityReceived ?? quantityReceived,
        faultyQuantity: prev[productId]?.faultyQuantity ?? faultyQuantity,
      },
    }));
  };

  const removeProductFromNotInOrderIfUnused = (productId, exceptItemId) => {
    const stillLinked = Object.entries(lineSubstitutes).some(
      ([itemId, productIds]) => itemId !== exceptItemId && (productIds || []).includes(productId)
    );
    if (stillLinked) return;
    setSelectedProducts((prev) => prev.filter((id) => id !== productId));
    setProductsNotInOrder((prev) => {
      const next = { ...prev };
      delete next[productId];
      return next;
    });
    setSelectedProductDetails((prev) => {
      const next = { ...prev };
      delete next[productId];
      return next;
    });
  };

  const handleDifferentSkuChange = (itemId, checked) => {
    const orderItem = orderItems?.find((item) => item._id === itemId);
    const sentQuantity = orderItem?.quantityOrdered || orderItem?.quantity || 0;

    setDifferentSkuByItem((prev) => ({ ...prev, [itemId]: checked }));

    if (checked) {
      // Do not credit the AWB SKU — zero the order line qty
      applyOrderItemQuantity(itemId, 0);
      if (!selectedOrderItems.includes(itemId)) {
        setSelectedOrderItems((prev) => [...prev, itemId]);
      }
    } else {
      // Restore expected same-SKU receive qty; clear line substitutes
      applyOrderItemQuantity(itemId, sentQuantity);
      const previousSubstitutes = lineSubstitutes[itemId] || [];
      setLineSubstitutes((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
      previousSubstitutes.forEach((productId) => {
        removeProductFromNotInOrderIfUnused(productId, itemId);
      });
    }
  };

  const handleLineSubstitutesChange = (itemId, selectedProductsList) => {
    const orderItem = orderItems?.find((item) => item._id === itemId);
    const sentQuantity = orderItem?.quantityOrdered || orderItem?.quantity || 0;
    const previousIds = lineSubstitutes[itemId] || [];
    const nextIds = (selectedProductsList || []).map((p) => p._id).filter(Boolean);

    setLineSubstitutes((prev) => ({ ...prev, [itemId]: nextIds }));

    previousIds
      .filter((id) => !nextIds.includes(id))
      .forEach((productId) => removeProductFromNotInOrderIfUnused(productId, itemId));

    (selectedProductsList || []).forEach((product) => {
      ensureProductInNotInOrder(product, sentQuantity || 1, 0);
    });
  };

  const handleProductQuantityChange = (productId, value) => {
    const numValue = parseInt(value) || 0;
    setProductsNotInOrder((prev) => {
      const current = prev[productId] || { quantityReceived: 0, faultyQuantity: 0 };
      const quantityReceived = Math.max(0, numValue);
      return {
        ...prev,
        [productId]: {
          ...current,
          quantityReceived,
          faultyQuantity: clampFaultyQuantity(quantityReceived, current.faultyQuantity),
        },
      };
    });
  };

  const handleProductFaultyQuantityChange = (productId, value) => {
    setProductsNotInOrder((prev) => {
      const current = prev[productId] || { quantityReceived: 1, faultyQuantity: 0 };
      return {
        ...prev,
        [productId]: {
          ...current,
          faultyQuantity: clampFaultyQuantity(current.quantityReceived, value),
        },
      };
    });
  };

  const handleOrderItemSelection = (itemId, checked) => {
    if (checked) {
      setSelectedOrderItems([...selectedOrderItems, itemId]);
      // Initialize data if not exists
      if (!orderItemData[itemId]) {
        const item = orderItems.find(i => i._id === itemId);
        setOrderItemData({
          ...orderItemData,
          [itemId]: {
            quantityReceived: item?.quantityOrdered || item?.quantity || 0,
            faultyQuantity: 0
          }
        });
      }
    } else {
      setSelectedOrderItems(selectedOrderItems.filter(id => id !== itemId));
      if (differentSkuByItem[itemId]) {
        handleDifferentSkuChange(itemId, false);
      }
    }
  };

  const handleProductSelection = (productIds) => {
    setSelectedProducts(productIds);
    setProductsNotInOrder((prev) => {
      const next = { ...prev };
      productIds.forEach((productId) => {
        if (!next[productId]) {
          next[productId] = { quantityReceived: 1, faultyQuantity: 0 };
        }
      });
      Object.keys(next).forEach((productId) => {
        if (!productIds.includes(productId)) {
          delete next[productId];
        }
      });
      return next;
    });
    setSelectedProductDetails((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((productId) => {
        if (!productIds.includes(productId)) {
          delete next[productId];
        }
      });
      return next;
    });
    setLineSubstitutes((prev) => {
      const next = {};
      Object.entries(prev).forEach(([itemId, ids]) => {
        next[itemId] = (ids || []).filter((id) => productIds.includes(id));
      });
      return next;
    });
  };

  const handleProductSelect = (record, selected) => {
    if (selected && record.product) {
      setSelectedProductDetails((prev) => ({
        ...prev,
        [record.key]: record.product,
      }));
    }
  };

  const handleProductSelectAll = (selected, _selectedRows, changeRows) => {
    setSelectedProductDetails((prev) => {
      const next = { ...prev };
      changeRows.forEach((record) => {
        if (selected && record.product) {
          next[record.key] = record.product;
        } else {
          delete next[record.key];
        }
      });
      return next;
    });
  };

  const handleProductDeselect = (productId) => {
    handleProductSelection(selectedProducts.filter((id) => id !== productId));
    setLineSubstitutes((prev) => {
      const next = {};
      Object.entries(prev).forEach(([itemId, productIds]) => {
        next[itemId] = (productIds || []).filter((id) => id !== productId);
      });
      return next;
    });
  };

  const handleProductModalOpen = (product) => {
    setSelectedProductForModal(product);
    setProductModalVisible(true);
  };

  const handleProductModalClose = () => {
    setProductModalVisible(false);
    setSelectedProductForModal(null);
  };

  const handleSearch = () => {
    setProductsPage(1);
    if (order && order.vendor) {
      getProducts({ 
        page: 1, 
        limit: 100, 
        vendorId: order.vendor._id || order.vendor,
        search: search || undefined
      });
    }
  };

  const buildReceivePayload = () => {
    if (!order) return null;

    const payload = buildRtoReceivePayload({
      orderId: order._id,
      vendorId: order.vendor?._id || order.vendor,
      orderItems,
      selectedOrderItemIds: selectedOrderItems,
      orderItemData,
      differentSkuByItem,
      selectedProductIds: selectedProducts,
      productsNotInOrder,
    });

    return {
      orderItemsArray: payload.orderItems,
      productsNotInOrderArray: payload.productsNotInOrder,
      vendorId: payload.vendorId,
      orderId: payload.orderId,
    };
  };

  const submitReceive = (payload) => {
    const data = {
      orderId: payload.orderId,
      vendorId: payload.vendorId,
      orderItems: payload.orderItemsArray,
      productsNotInOrder: payload.productsNotInOrderArray,
    };
    actionRTOInbound(data, navigate);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const built = buildReceivePayload();
    if (!built) return;

    const { orderItemsArray, productsNotInOrderArray } = built;

    if (orderItemsArray.length === 0 && productsNotInOrderArray.length === 0) {
      notification.error({
        message: "Select at least one received item (AWB line and/or product not on AWB).",
      });
      return;
    }

    // Wrong-SKU lines must have a substitute product selected
    const differentSkuItemIds = Object.keys(differentSkuByItem).filter(
      (id) => differentSkuByItem[id] && selectedOrderItems.includes(id)
    );
    for (const itemId of differentSkuItemIds) {
      const substitutes = lineSubstitutes[itemId] || [];
      if (substitutes.length === 0) {
        notification.error({
          message: "Received different SKU: select the product that was actually received for each swapped AWB line.",
        });
        return;
      }
    }

    // Guard against double-crediting: substitutes present while AWB lines still have positive qty
    const hasPositiveAwbQty = orderItemsArray.some((item) => item.quantityReceived > 0);
    if (productsNotInOrderArray.length > 0 && hasPositiveAwbQty) {
      setPendingSubmitPayload(built);
      setDoubleCreditConfirmOpen(true);
      return;
    }

    submitReceive(built);
  };

  const handleConfirmDoubleCreditSubmit = () => {
    if (pendingSubmitPayload) {
      submitReceive(pendingSubmitPayload);
    }
    setDoubleCreditConfirmOpen(false);
    setPendingSubmitPayload(null);
  };

  const handleCancelDoubleCreditSubmit = () => {
    setDoubleCreditConfirmOpen(false);
    setPendingSubmitPayload(null);
  };

  // Get order item product IDs to filter them out from partner products
  const orderItemProductIds = orderItems?.map(item => {
    const productId = item.product?._id || item.stockItem?.product?._id || item.stockItem?.product;
    return productId ? String(productId) : null;
  }).filter(Boolean) || [];

  // Filter products to show only those not in order
  const availableProducts = products?.docs?.filter(product => 
    product._id && !orderItemProductIds.includes(String(product._id))
  ) || [];

  const getLineSubstituteProducts = (itemId) => {
    const ids = lineSubstitutes[itemId] || [];
    return ids
      .map((id) => selectedProductDetails[id] || availableProducts.find((p) => p._id === id))
      .filter(Boolean);
  };

  return (
    <Container maxWidth="xl">
      <Box
        sx={{
          paddingTop: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Box sx={{ width: "100%", mt: 2, px: 2 }}>
          <Typography mt={2} variant="h4" textAlign={"center"} sx={{ color: "var(--secColor)" }}>
            Take Action on RTO
          </Typography>

          {/* Order Information */}
          {order && (
            <>
              <Card sx={{ mb: 3, mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, color: "var(--secColor)" }}>
                    Order Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="body2" color="text.secondary">
                        Order ID
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {order.externalId || order._id}
                      </Typography>
                    </Grid>
                    {order.shopifyOrderNumber && (
                      <Grid item xs={6} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Shopify Order
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {order.shopifyOrderNumber}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={6} sm={4}>
                      <Typography variant="body2" color="text.secondary">
                        Status
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {order.status}
                      </Typography>
                    </Grid>
                    {order.bostaTrackingId && (
                      <Grid item xs={6} sm={4}>
                        <Typography variant="body2" color="text.secondary">
                          Bosta Tracking ID
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {order.bostaTrackingId}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={6} sm={4}>
                      <Typography variant="body2" color="text.secondary">
                        Created At
                      </Typography>
                      <Typography variant="body1">
                        {moment(order.createdAt).format("lll")}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant="body2" color="text.secondary">
                        Financial Status
                      </Typography>
                      <Typography variant="body1">
                        {order.financialStatus}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Order Items (AWB lines) */}
              {orderItems && orderItems.length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, color: "var(--secColor)" }}>
                      Order Items (AWB)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      For each AWB line, enter the quantity received of that SKU. If the box contained a different product, mark &quot;Received different SKU&quot; — that zeros the AWB line and credits the substitute instead.
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    {orderItems.map((item) => {
                      const sentQuantity = item.quantityOrdered || item.quantity || 0;
                      const isSelected = selectedOrderItems.includes(item._id);
                      const itemData = orderItemData[item._id] || { quantityReceived: sentQuantity, faultyQuantity: 0 };
                      const product = item.product || item.stockItem?.product;
                      const exceedsSent = itemData.quantityReceived > sentQuantity;
                      const isDifferentSku = !!differentSkuByItem[item._id];

                      return (
                        <Card key={item._id} sx={{ mb: 2, p: 2, opacity: isSelected ? 1 : 0.6 }}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={1} sx={{ ...centered_flex_box }}>
                              <Checkbox
                                checked={isSelected}
                                onChange={(e) => handleOrderItemSelection(item._id, e.target.checked)}
                              />
                            </Grid>
                            <Grid item xs={12} sm={2} sx={{ ...centered_flex_box }}>
                              {product?.image && (
                                <Avatar
                                  src={product.image}
                                  sx={{ width: 80, height: 80 }}
                                  variant="rounded"
                                />
                              )}
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Typography variant="body1" fontWeight="bold">
                                {product?.name || "N/A"}
                              </Typography>
                              {product?.sku && (
                                <Typography variant="body2" color="text.secondary">
                                  SKU: {product.sku}
                                </Typography>
                              )}
                              {product?.externalId && (
                                <Typography variant="body2" color="text.secondary">
                                  External ID: {product.externalId}
                                </Typography>
                              )}
                              {product?.size && (
                                <Typography variant="body2">Size: {product.size}</Typography>
                              )}
                              {product?.color && (
                                <Typography variant="body2">Color: {product.color}</Typography>
                              )}
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Price: {item.price} EGP
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Sent Quantity: {sentQuantity}
                              </Typography>
                              {isDifferentSku && (
                                <Chip
                                  label="Received different SKU"
                                  size="small"
                                  color="warning"
                                  sx={{ mt: 1 }}
                                />
                              )}
                            </Grid>
                            {isSelected && (
                              <>
                                <Grid item xs={12} sm={4}>
                                  {!isDifferentSku ? (
                                    <InboundFaultySplitFields
                                      quantityReceived={itemData.quantityReceived}
                                      faultyQuantity={itemData.faultyQuantity}
                                      quantityMin={0}
                                      quantityHelperText={
                                        exceedsSent ? `Exceeds sent (${sentQuantity})` : undefined
                                      }
                                      onQuantityChange={(qty) => handleOrderItemQuantityChange(item._id, qty)}
                                      onFaultyChange={(qty) => handleOrderItemFaultyQuantityChange(item._id, qty)}
                                      dense
                                    />
                                  ) : (
                                    <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                                      AWB line qty set to 0 — credit substitutes below
                                    </Typography>
                                  )}
                                  <FormControlLabel
                                    sx={{ mt: 1, ml: 0 }}
                                    control={
                                      <Checkbox
                                        checked={isDifferentSku}
                                        onChange={(e) => handleDifferentSkuChange(item._id, e.target.checked)}
                                      />
                                    }
                                    label="Received different SKU"
                                  />
                                </Grid>
                                {isDifferentSku && (
                                  <Grid item xs={12}>
                                    <Alert severity="info" sx={{ mb: 1 }}>
                                      Select the SKU(s) actually received. Those products are credited as Not on AWB; this AWB line is submitted with quantity 0 so the original SKU is not credited.
                                    </Alert>
                                    <Autocomplete
                                      multiple
                                      options={[
                                        ...getLineSubstituteProducts(item._id).filter(
                                          (p) => !availableProducts.some((a) => a._id === p._id)
                                        ),
                                        ...availableProducts,
                                      ]}
                                      value={getLineSubstituteProducts(item._id)}
                                      onChange={(_, value) => handleLineSubstitutesChange(item._id, value)}
                                      getOptionLabel={(p) =>
                                        `(${p.sku || p.externalId || p._id}) ${p.name || ""}`
                                      }
                                      isOptionEqualToValue={(option, value) => option._id === value._id}
                                      renderInput={(params) => (
                                        <MainInput
                                          {...params}
                                          margin="normal"
                                          fullWidth
                                          label="Actually received product(s)"
                                          placeholder="Search / select substitute SKU"
                                        />
                                      )}
                                    />
                                  </Grid>
                                )}
                              </>
                            )}
                          </Grid>
                        </Card>
                      );
                    })}
                  </CardContent>
                </Card>
              )}

              {/* Products not on AWB */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ ...centered_flex_box, mb: 2, justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h6" sx={{ color: "var(--secColor)" }}>
                        Not on AWB
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Products physically received that were not on the AWB / original order. Stock is credited here only.
                      </Typography>
                    </Box>
                    <Button
                      onClick={() => setShowFilters(!showFilters)}
                      sx={{ ...main_button, ml: 2 }}
                    >
                      {showFilters ? <FilterListOff sx={{ color: "var(--secondaryColor)" }} /> : <FilterList sx={{ color: "var(--secColor4)" }} />}
                      Filters
                    </Button>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Collapse in={showFilters}>
                    <Box sx={{ mb: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography>Search</Typography>
                          <MainInput
                            margin="normal"
                            fullWidth
                            placeholder="Search by SKU or product name"
                            value={search}
                            onChange={({ target }) => setSearch(target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleSearch();
                              }
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={handleSearch}
                                    edge="end"
                                    sx={{ color: "var(--secColor4)" }}
                                  >
                                    <Search />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Collapse>
                  {selectedProducts.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ mb: 2, color: "var(--secColor)", fontWeight: "bold" }}>
                        Selected — Not on AWB ({selectedProducts.length})
                      </Typography>
                      {selectedProducts.map((productId) => {
                        const product = selectedProductDetails[productId];
                        const productData = productsNotInOrder[productId] || { quantityReceived: 1, faultyQuantity: 0 };
                        const productFaultyQty = clampFaultyQuantity(productData.quantityReceived, productData.faultyQuantity);
                        const productOkQty = okQuantityFromReceive(productData.quantityReceived, productData.faultyQuantity);
                        const splitLabel =
                          productFaultyQty === 0
                            ? `${productOkQty} Available`
                            : productFaultyQty === productData.quantityReceived
                              ? `${productFaultyQty} Returns`
                              : `${productOkQty} Available · ${productFaultyQty} Returns`;

                        return (
                          <Card key={productId} sx={{ mb: 2, p: 2 }}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12} sm={2} sx={{ ...centered_flex_box }}>
                                {product?.image ? (
                                  <Avatar
                                    src={product.image}
                                    sx={{ width: 64, height: 64 }}
                                    variant="rounded"
                                  />
                                ) : (
                                  <Avatar sx={{ width: 64, height: 64 }} variant="rounded" />
                                )}
                              </Grid>
                              <Grid item xs={12} sm={5}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                  <Typography variant="body1" fontWeight="bold">
                                    {product?.name || "Product"}
                                  </Typography>
                                  <Chip label="Not on AWB" size="small" variant="outlined" />
                                </Box>
                                {(product?.sku || product?.externalId) && (
                                  <Typography variant="body2" color="text.secondary">
                                    SKU: {product.sku || product.externalId}
                                  </Typography>
                                )}
                                {product?.size && (
                                  <Typography variant="body2">Size: {product.size}</Typography>
                                )}
                                {product?.color && (
                                  <Typography variant="body2">Color: {product.color}</Typography>
                                )}
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <Typography variant="body2" color="text.secondary">
                                  Received {productData.quantityReceived}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                  {splitLabel}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={2} sx={{ ...centered_flex_box, gap: 1 }}>
                                {product && (
                                  <AntButton
                                    type="primary"
                                    size="small"
                                    onClick={() => handleProductModalOpen(product)}
                                  >
                                    {productsNotInOrder[productId] ? "Edit" : "Configure"}
                                  </AntButton>
                                )}
                                <IconButton
                                  size="small"
                                  onClick={() => handleProductDeselect(productId)}
                                  aria-label="Remove product"
                                  sx={{ color: "var(--secColor)" }}
                                >
                                  <Close />
                                </IconButton>
                              </Grid>
                            </Grid>
                          </Card>
                        );
                      })}
                    </Box>
                  )}
                  <Box sx={antTableWrapperSx(isMobile)}>
                  <Table
                    columns={[
                      {
                        title: 'Image',
                        dataIndex: 'image',
                        render: (text) => <ImageComponent width={50} height={50} src={text} preview={true} />,
                      },
                      {
                        title: 'Product SKU',
                        dataIndex: '_id',
                      },
                      {
                        title: 'Name',
                        dataIndex: 'name',
                      },
                      {
                        title: 'Size',
                        dataIndex: 'size',
                      },
                      {
                        title: 'Color',
                        dataIndex: 'color',
                      },
                      {
                        title: 'Price',
                        dataIndex: 'price',
                        sorter: (a, b) => (a.price || 0) - (b.price || 0),
                        render: (price) => price ? `${price} EGP` : 'N/A'
                      },
                      {
                        title: 'Quantity',
                        dataIndex: 'quantity',
                        render: (_, record) => {
                          const productData = productsNotInOrder[record.key];
                          return productData ? productData.quantityReceived : '-';
                        },
                      },
                      {
                        title: 'Faulty',
                        dataIndex: 'faulty',
                        render: (_, record) => {
                          const productData = productsNotInOrder[record.key];
                          if (!productData) return '-';
                          const fq = clampFaultyQuantity(productData.quantityReceived, productData.faultyQuantity);
                          if (fq === 0) return 'None';
                          if (fq === productData.quantityReceived) return 'All';
                          return `${fq} of ${productData.quantityReceived}`;
                        },
                      },
                      {
                        title: 'Actions',
                        render: (_, record) => (
                          <Space>
                            <AntButton 
                              type="primary" 
                              size="small"
                              onClick={() => handleProductModalOpen(record.product)}
                              disabled={!selectedProducts.includes(record.key)}
                            >
                              {productsNotInOrder[record.key] ? 'Edit' : 'Configure'}
                            </AntButton>
                          </Space>
                        ),
                      },
                    ]}
                    dataSource={availableProducts.map((product) => ({
                      key: product._id,
                      _id: product.sku || product.externalId,
                      image: product.image,
                      name: product.name,
                      size: product.size,
                      color: product.color,
                      price: product.price,
                      product: product,
                    }))}
                    rowSelection={{
                      selectedRowKeys: selectedProducts,
                      onChange: handleProductSelection,
                      onSelect: handleProductSelect,
                      onSelectAll: handleProductSelectAll,
                      preserveSelectedRowKeys: true,
                    }}
                    loading={isLoading}
                    showSorterTooltip={{
                      target: 'sorter-icon',
                    }}
                    pagination={defaultPaginatedTableProps({
                      current: productsPage,
                      pageSize: parseInt(products?.limit) || 100,
                      total: products?.totalDocs,
                      onChange: (nextPage) => setProductsPage(nextPage),
                    })}
                    scroll={{ x: isMobile ? 300 : 1200 }}
                  />
                  </Box>
                </CardContent>
              </Card>

              {/* Product Modal for Quantity and Faulty Status */}
              <Modal
                title="Set Not-on-AWB Product Details"
                open={productModalVisible}
                onOk={handleProductModalClose}
                onCancel={handleProductModalClose}
                okText="Done"
              >
                {selectedProductForModal && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {selectedProductForModal.name}
                    </Typography>
                    <Chip label="Not on AWB" size="small" sx={{ mb: 2 }} />
                    <InboundFaultySplitFields
                      quantityReceived={productsNotInOrder[selectedProductForModal._id]?.quantityReceived || 1}
                      faultyQuantity={productsNotInOrder[selectedProductForModal._id]?.faultyQuantity || 0}
                      quantityMin={0}
                      onQuantityChange={(qty) => handleProductQuantityChange(selectedProductForModal._id, qty)}
                      onFaultyChange={(qty) => handleProductFaultyQuantityChange(selectedProductForModal._id, qty)}
                    />
                  </Box>
                )}
              </Modal>

              {/* Submit Button */}
              <Box component="form" onSubmit={handleSubmit}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2, ...main_button }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <CircularProgress sx={{ color: "white" }} />
                  ) : (
                    "Take Action on RTO"
                  )}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Confirmation Dialog when received qty exceeds sent qty */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelQuantityUpdate}
        aria-labelledby="rto-qty-alert-dialog-title"
        aria-describedby="rto-qty-alert-dialog-description"
      >
        <DialogTitle id="rto-qty-alert-dialog-title">
          Quantity Exceeds Sent Amount
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="rto-qty-alert-dialog-description">
            You are setting a received quantity of {pendingQuantityUpdate.quantity}, which is more than the sent quantity ({pendingQuantityUpdate.sentQuantity}). Please confirm this is correct.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelQuantityUpdate} sx={{ ...sec_button }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmQuantityUpdate} sx={{ ...main_button }} autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Guard: substitutes + positive AWB qty can double-credit stock */}
      <Dialog
        open={doubleCreditConfirmOpen}
        onClose={handleCancelDoubleCreditSubmit}
        aria-labelledby="rto-double-credit-dialog-title"
        aria-describedby="rto-double-credit-dialog-description"
      >
        <DialogTitle id="rto-double-credit-dialog-title">
          Confirm stock credit
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="rto-double-credit-dialog-description">
            You have products marked Not on AWB and AWB lines with quantity received greater than 0.
            Stock will be credited for both. If a different SKU replaced an AWB line, mark that line as
            &quot;Received different SKU&quot; (zeros the AWB qty) so only the substitute is credited.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDoubleCreditSubmit} sx={{ ...sec_button }}>
            Go back
          </Button>
          <Button onClick={handleConfirmDoubleCreditSubmit} sx={{ ...main_button }} autoFocus>
            Credit both
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  user: state?.auth?.user,
  isLoading: state?.records?.isLoading || state?.wait?.isLoading,
  order: state?.records?.order,
  products: state?.records?.products
});

const mapDispatchToProps = { getOrders, actionRTOInbound, getProducts };

export default connect(mapStateToProps, mapDispatchToProps)(TakeActionRTO);

